// The **CustomFittingTechnology** component is a responsive carousel that displays custom fitting technology details.
// It dynamically adjusts the number of visible slides based on screen size, supporting desktop, tablet, and mobile
// views. Users can navigate through the slides using left and right buttons, with seamless looping. The component ensures
// accessibility by supporting keyboard navigation and screen reader-friendly labels. Each slide contains an image, title,
// and description, providing an interactive and visually appealing user experience.

import React, { useState, useMemo } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/solid';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import { desktop, tablet, mobile } from 'helpers/utils/screensizes';
import { CustomFitTechnology } from 'types/next';

interface CustomFitProps {
  customFitTechnology: CustomFitTechnology;
}

const CustomFittingTechnology = ({ customFitTechnology }: CustomFitProps) => {
  const contentItems = customFitTechnology?.content || [];
  const totalItems = contentItems.length;
  const [currentIndex, setCurrentIndex] = useState(0);

  // Responsive slides per view
  const isTablet = useMediaQuery(tablet);
  const isDesktop = useMediaQuery(desktop);
  const isMobile = useMediaQuery(mobile);

  const slidesPerView = useMemo(() => {
    const isMobileView = isMobile?.[0] ?? false;
    const isTabletView = isTablet?.[0] ?? false;
    const isDesktopView = isDesktop?.[0] ?? false;

    if (isDesktopView) return 3;
    if (isTabletView) return 3;
    if (isMobileView) return 1;
    return 1; // Default for mobile
  }, [isDesktop, isTablet, isMobile]);

  const totalSlides = contentItems.length;

  // Move to previous slide (shift left by 1)
  const handlePrev = () => {
    setCurrentIndex((prevIndex) => (prevIndex === 0 ? totalSlides - slidesPerView : prevIndex - 1));
  };

  // Move to next slide (shift right by 1)
  const handleNext = () => {
    setCurrentIndex((prevIndex) => (prevIndex + slidesPerView >= totalSlides ? 0 : prevIndex + 1));
  };

  return (
    <div className="customfittechnology-section mb-30  py-10">
      <div className="flex flex-col items-center gap-y-10 px-10 pb-10">
        <h3 className="mx-auto w-[80%] text-center font-body text-24 font-normal leading-[40px] text-[#1A1871]  lg:text-38">
          {customFitTechnology?.overallHeading}
        </h3>
      </div>
      <hr className="mx-auto my-20 mb-40 w-[90%] max-w-[960px] overflow-hidden  border-[1.2px] border-t border-[#cfc9c4]" />

      <div className="relative z-10 mx-auto max-w-[1140px]">
        {/* Navigation Buttons */}
        <div className="absolute left-0 right-0 top-1/2 flex -translate-y-1/2 justify-between px-4">
          <button
            className="left-[-30px] flex h-35 w-35 items-center justify-center  bg-white shadow-md transition-all duration-300"
            onClick={handlePrev}
          >
            <ChevronLeftIcon className="text-bold -ml-6 h-30 w-30 text-[#1A4075]" />
          </button>
          <button
            className="right-[-30px] flex h-35 w-35 items-center justify-center bg-white shadow-md transition-all duration-300 "
            onClick={handleNext}
          >
            <ChevronRightIcon className="ml-6 h-30 w-30 text-[#1A4075]" />
          </button>
        </div>

        {/* Carousel Items */}
        <div className="mx-auto flex flex-wrap justify-center gap-4 overflow-hidden p-20 transition-transform duration-300 ease-in-out md:flex-nowrap md:space-x-20">
          {contentItems?.slice(currentIndex, currentIndex + slidesPerView).map((item: any, index: number) => (
            <div
              key={index}
              className="group p-1 transition-all duration-300 ease-in-out hover:border-[1px] hover:border-dotted hover:border-black"
            >
              <div className="group flex min-h-[368.77px] max-w-[360px] flex-col bg-[#1A4075] p-6  text-center text-white shadow-lg transition duration-300 ease-in-out hover:bg-[#FFFFFF] hover:text-black md:h-[402px] lg:h-[368px] xl:h-[368px]">
                <div className="  flex h-[120px] w-full items-center justify-center py-5">
                  <img
                    src={`https://americangolf.a.bigcontent.io/v1/static/${item.image.name}`}
                    alt={item.title}
                    className="max-h-full object-contain transition duration-300 ease-in-out group-hover:brightness-0"
                  />
                </div>
                <div className="text-center">
                  <h2 className="group-hover:text-black-300 mt-4 py-[10px] text-[1.2rem] font-bold uppercase tracking-[1px] transition duration-150 ease-in-out group-hover:tracking-[5px] ">
                    {item.title}
                  </h2>
                  <p className="mt-2 p-10 text-16 font-normal leading-[19.2px]">{item.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="mt-5 text-center font-body text-lg font-medium text-gray-700">
        <em> {customFitTechnology?.contactInfo}</em>
      </div>
    </div>
  );
};

export default CustomFittingTechnology;
